<template>
  <div
    class="w-full flex"
    style="min-height: 764px;"
  >
    <div class="flex flex-col justify-between w-80 flex-shrink-0 border-r border-border-normal p-7">
      <div class="flex flex-col">
        <div
          v-for="(step, index) in steps"
          :key="step.key"
          class="flex flex-col"
        >
          <div class="flex items-center gap-1.5">
            <div
              class="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0"
              :class="stepIndicatorClass(index)"
            >
              <div
                v-if="isStepComplete(index)"
                class="rounded-full w-7 h-7 p-1 flex items-center justify-center bg-primary-green-10"
              >
                <CheckmarkIcon
                  class-name="text-secondary-green-300 border border-secondary-green-300 rounded-full w-full h-full"
                />
              </div>
              <BaseText
                v-else
                type="label"
                size="sm"
                class="text-center w-5"
                :class="index === currentStepIndex ? 'text-text-muted' : 'text-text-normal'"
              >
                {{ index + 1 }}
              </BaseText>
            </div>
            <div class="flex items-center justify-center px-1">
              <BaseText
                type="label"
                size="sm"
                class="text-text-muted whitespace-nowrap"
              >
                {{ step.label }}
              </BaseText>
            </div>
          </div>
          <div
            v-if="index < steps.length - 1"
            class="step-connector w-7 flex items-center justify-center"
          >
            <div class="w-px h-full bg-border-normal" />
          </div>
        </div>
      </div>

      <div class="flex flex-col gap-3">
        <!-- <div class="flex flex-col gap-3 p-3 rounded-xl bg-white border border-border-normal">
          <div class="flex items-center">
            <div class="w-[18px] h-[18px] rounded-full bg-neutral-200 border border-white -mr-1.5" />
            <div class="w-[18px] h-[18px] rounded-full bg-neutral-300 border border-white" />
          </div>
          <div class="flex flex-col gap-1">
            <BaseText
              type="label"
              size="sm"
              class="text-text-muted"
            >
              1:1 Recovery Support
            </BaseText>
            <BaseText
              type="body"
              size="sm"
              class="text-text-normal"
            >
              Let us set up your recovery system.
            </BaseText>
          </div>
        </div> -->
        <div class="flex items-center gap-2 p-1 rounded-3xl">
          <BaseText
            type="body"
            size="sm"
            class="text-text-normal whitespace-nowrap"
          >
            Powered by
          </BaseText>
          <img
            src="../../../assets/images/spendback-logo.png"
            alt="Spendback Logo"
            class="h-3 w-auto"
          >
        </div>
      </div>
    </div>

    <div class="flex-1 flex justify-center py-7">
      <div class="w-full max-w-[768px] flex flex-col gap-7">
        <component
          :is="currentStepComponent"
          v-bind="currentStepProps"
          @continue="handleContinue"
        />
      </div>
    </div>
  </div>
</template>

<script>
import LensAPI from '@/api/lens/LensRequests'
import SpendBackStepProperty from './steps/SpendBackStepProperty.vue'
import SpendBackStepAccounts from './steps/SpendBackStepAccounts.vue'
import SpendBackStepPixel from './steps/SpendBackStepPixel.vue'
import SpendBackStepEmail from './steps/SpendBackStepEmail.vue'

// Icons
import CheckmarkIcon from '../../globals/Icons/CheckmarkIcon.vue'
export default {
  name: 'LensSpendBackOnboarding',
  components: {
    CheckmarkIcon,
    SpendBackStepProperty,
    SpendBackStepAccounts,
    SpendBackStepPixel,
    SpendBackStepEmail
  },
  props: {
    lensId: {
      type: String,
      required: true
    },
    lens: {
      type: Object,
      default: () => ({})
    },
    accounts: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    },
    initialOnboardStatus: {
      type: String,
      default: null
    },
    propertyId: {
      type: String,
      default: null
    }
  },
  data () {
    return {
      currentStepIndex: 0,
      completedSteps: [],
      statusInitialized: false,
      submitting: false,
      steps: [
        { key: 'create_property', label: 'Create Property', component: 'SpendBackStepProperty' },
        { key: 'install_pixel', label: 'Install Pixel', component: 'SpendBackStepPixel' },
        { key: 'connect_accounts', label: 'Connected Accounts', component: 'SpendBackStepAccounts' },
        { key: 'delegate_email', label: 'Delegate Email', component: 'SpendBackStepEmail' }
      ]
    }
  },
  computed: {
    currentStep () {
      return this.steps[this.currentStepIndex]
    },
    currentStepComponent () {
      return this.currentStep.component
    },
    nextStepLabel () {
      const next = this.steps[this.currentStepIndex + 1]
      return next ? next.label : null
    },
    currentStepProps () {
      const base = {
        lensId: this.lensId,
        lens: this.lens,
        nextStepLabel: this.nextStepLabel,
        isLastStep: this.currentStepIndex === this.steps.length - 1,
        submitting: this.submitting
      }
      if (this.currentStep.key === 'connect_accounts') {
        base.accounts = this.accounts
        base.loading = this.loading
      }
      return base
    }
  },
  watch: {
    initialOnboardStatus: {
      immediate: true,
      handler (status) {
        this.initFromStatus(status)
      }
    }
  },
  methods: {
    initFromStatus (status) {
      if (this.statusInitialized || !status) return
      const lastCompletedIndex = this.steps.findIndex(step => step.key === status)
      if (lastCompletedIndex === -1) return

      this.completedSteps = this.steps
        .map((_, index) => index)
        .filter(index => index <= lastCompletedIndex)
      this.currentStepIndex = Math.min(lastCompletedIndex + 1, this.steps.length - 1)
      this.statusInitialized = true
    },
    isStepComplete (index) {
      return this.completedSteps.includes(index)
    },
    stepIndicatorClass (index) {
      if (this.isStepComplete(index)) return 'bg-primary-black-100'
      if (index === this.currentStepIndex) return 'bg-background-normal'
      return ''
    },
    async handleStepRequest (stepData) {
      if (this.currentStep.key === 'create_property') {
        await LensAPI.SpendBack.createProperty(this.lensId, {
          name: stepData.propertyName,
          site_url: stepData.siteUrl
        })
      }
      if (this.currentStep.key === 'connect_accounts') {
        await LensAPI.SpendBack.createConnectLink(this.lensId, 'meta_ads')
      }
    },
    validateStepData (stepData) {
      if (this.currentStep.key === 'create_property' && (!stepData?.propertyName || !stepData?.siteUrl)) {
        this.$showAlert({ type: 'error', message: 'Please fill in all fields' })
        return false
      }
      return true
    },
    async handleContinue (stepData) {
      if (this.submitting) return

      if (!this.validateStepData(stepData)) return

      this.submitting = true
      try {
        await this.handleStepRequest(stepData)

        if (!this.completedSteps.includes(this.currentStepIndex)) {
          this.completedSteps.push(this.currentStepIndex)
        }
        this.$emit('step-complete', {
          stepKey: this.currentStep.key,
          stepIndex: this.currentStepIndex,
          data: stepData || {}
        })

        if (this.currentStepIndex < this.steps.length - 1) {
          this.currentStepIndex++
        } else {
          this.$emit('complete')
        }
      } catch (error) {
        console.error(error)
        this.$showAlert({
          type: 'error',
          message: error?.response?.data?.message || 'Something went wrong. Please try again.'
        })
      } finally {
        this.submitting = false
      }
    }
  }
}
</script>

<style scoped>
.step-connector {
  height: 28px;
}
</style>
